import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { formatApiError } from '@/utils/apiError';
import { gbp } from '@/components/sales/SalesOrderDialog';

const todayIso = () => new Date().toISOString().slice(0, 10);
const blank = () => ({ date: todayIso(), amount: '', paid_by: '', purpose: '', note: '', order_id: '' });

/** Record (or, for admins, edit) one cash payment. */
export function CashEntryDialog({ open, onClose, onSaved, entry = null, orders = [] }) {
  const [form, setForm] = useState(blank());
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm(entry ? { date: entry.date, amount: String(entry.amount), paid_by: entry.paid_by, purpose: entry.purpose, note: entry.note || '', order_id: entry.order_id || '' } : blank()); }, [open, entry]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const pickOrder = (id) => { const o = orders.find((x) => x.id === id); setForm((f) => ({ ...f, order_id: id, paid_by: f.paid_by || o?.customer || '', purpose: f.purpose || o?.description || '', amount: f.amount || (o ? String(o.sale_gross) : '') })); };
  const canSave = !busy && form.date && Number(form.amount) > 0 && form.paid_by.trim() && form.purpose.trim();

  const save = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/cash-log${entry ? `/${entry.id}` : ''}`, { method: entry ? 'PUT' : 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, amount: Number(form.amount), order_id: form.order_id || null, note: form.note || null }) });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiError(d) || 'Save failed');
      toast.success(entry ? 'Cash entry updated' : `Recorded ${gbp(d.amount)} cash from ${d.paid_by}`);
      onSaved(d); onClose();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-md" data-testid="cash-entry-dialog">
        <DialogHeader><DialogTitle>{entry ? 'Edit cash entry' : 'Record cash received'}</DialogTitle><DialogDescription>Someone paid in cash — write it down here.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label htmlFor="cash-date">Date</Label><Input id="cash-date" type="date" value={form.date} onChange={set('date')} className="bg-white" data-testid="cash-date" /></div>
            <div><Label htmlFor="cash-amount">Amount (£)</Label><Input id="cash-amount" type="number" min="0.01" step="0.01" value={form.amount} onChange={set('amount')} placeholder="0.00" className="bg-white text-lg font-bold" autoFocus data-testid="cash-amount" /></div>
          </div>
          <div><Label htmlFor="cash-paid-by">Who paid</Label><Input id="cash-paid-by" value={form.paid_by} onChange={set('paid_by')} placeholder="e.g. Mr Smith" className="bg-white" data-testid="cash-paid-by" /></div>
          <div><Label htmlFor="cash-purpose">What for</Label><Input id="cash-purpose" value={form.purpose} onChange={set('purpose')} placeholder="e.g. Balance on driveway job" className="bg-white" data-testid="cash-purpose" /></div>
          {orders.length > 0 && (
            <div>
              <Label htmlFor="cash-order">Link to a logged order <span className="text-gray-400 font-normal">(optional)</span></Label>
              <select id="cash-order" value={form.order_id} onChange={(e) => pickOrder(e.target.value)} className="w-full h-10 rounded-md border border-input bg-white px-3 text-sm" data-testid="cash-order">
                <option value="">No linked order</option>
                {orders.map((o) => <option key={o.id} value={o.id}>{o.date} · {o.customer || o.description || o.brand} · {gbp(o.sale_gross)}</option>)}
              </select>
            </div>
          )}
          <div><Label htmlFor="cash-note">Note <span className="text-gray-400 font-normal">(optional)</span></Label><Textarea id="cash-note" rows={2} value={form.note} onChange={set('note')} className="bg-white" data-testid="cash-note" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={!canSave} className="bg-[#1f4030] hover:bg-[#16302a] text-white" data-testid="cash-save">{busy ? 'Saving…' : entry ? 'Save changes' : 'Record cash'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
